import { useState } from 'react';
import { useSearchParams, useParams, useNavigate, Link } from 'react-router-dom';
import { Loader2, Search, ArrowRight, AlertCircle, ShoppingCart } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from '@/components/ui/accordion';
import api from '@/services/api';
import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';
import { StoreHeader, StoreFooter } from '@/components/store';
import { useStore } from '@/hooks/useStore';

export default function OrderVerify() {
    const { subdomain } = useParams();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();

    // For Reseller Store layout
    const { store, theme, storeAppearance, platformInfo } = useStore(subdomain);

    const uid = searchParams.get('uid');
    const ref = searchParams.get('ref');

    const [loading, setLoading] = useState(false);
    const [verifiedOrders, setVerifiedOrders] = useState(null);
    const [formData, setFormData] = useState({
        name: '',
        phone: ''
    });

    const isMainStore = !subdomain;

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const { data } = await api.post('/orders/guest/verify', {
                uid,
                ref,
                name: formData.name,
                phone: formData.phone,
                subdomain
            });

            if (data.is_registered) {
                toast.info("This order belongs to a registered account. Please log in.");
                const loginPath = subdomain ? `/store/${subdomain}/login` : '/customer/login';
                const accountPath = subdomain ? `/store/${subdomain}/account?order=${ref}` : `/customer/account?order=${ref}`;
                navigate(`${loginPath}?redirect=${encodeURIComponent(accountPath)}`);
                return;
            }

            if (data.success && data.orders) {
                setVerifiedOrders(data.orders);
                toast.success("Identity verified.");
            } else {
                toast.error(data.error || "Details do not match our records.");
            }
        } catch (error) {
            console.error("Verification failed", error);
            toast.error(error.response?.data?.error || "Verification failed. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    const content = (
        <div className="flex-1 max-w-4xl mx-auto w-full px-4 py-12">
            {!verifiedOrders ? (
                <div className="max-w-md mx-auto bg-white p-8 rounded-2xl shadow-xl shadow-slate-200/50 border border-slate-100">
                    <div className="text-center mb-8">
                        <div className="w-16 h-16 bg-indigo-50 rounded-full flex items-center justify-center mx-auto mb-4">
                            <Search className="w-8 h-8 text-indigo-600" />
                        </div>
                        <h1 className="text-2xl font-bold text-slate-900">Verify Identity</h1>
                        <p className="text-slate-500 mt-2">Please enter the details used during checkout to view your order securely.</p>
                        <p className="text-xs text-slate-400 mt-2 break-all">Order ID: {ref}</p>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-6">
                        <div className="space-y-2">
                            <Label>Full Name</Label>
                            <Input
                                required
                                placeholder="E.g. Jane Doe"
                                value={formData.name}
                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                className="h-11 rounded-xl"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Phone Number</Label>
                            <Input
                                required
                                placeholder="E.g. 555-0198"
                                value={formData.phone}
                                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                                className="h-11 rounded-xl"
                            />
                        </div>
                        <Button
                            type="submit"
                            disabled={loading}
                            className="w-full h-11 rounded-xl font-bold transition-all text-white"
                            style={{ backgroundColor: theme?.primary_color || (isMainStore ? '#b45309' : '#4f46e5') }}
                        >
                            {loading ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : 'Verify & View Order'}
                            {!loading && <ArrowRight className="w-4 h-4 ml-2" />}
                        </Button>
                    </form>
                </div>
            ) : (
                <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
                    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                        <h1 className="text-3xl font-bold text-slate-900">Your Orders</h1>
                        <div className="text-sm text-slate-500 bg-white px-4 py-2 rounded-full border border-slate-200 shadow-sm flex items-center gap-2">
                            <AlertCircle className="w-4 h-4 text-emerald-500" /> Secure Guest Session
                        </div>
                    </div>

                    <div className="bg-white p-2 sm:p-6 rounded-2xl shadow-sm border border-slate-200">
                        <Accordion type="multiple" defaultValue={[`order-${ref}`]} className="w-full space-y-4">
                            {verifiedOrders.map((order) => (
                                <AccordionItem key={order._id} value={`order-${order.order_id}`} className="border border-slate-200 rounded-xl overflow-hidden bg-white">
                                    <AccordionTrigger className="px-6 py-4 hover:bg-slate-50 hover:no-underline transition-colors flex justify-between">
                                        <div className="flex flex-col md:flex-row md:items-center justify-between w-full pr-4 gap-4">
                                            <div className="text-left">
                                                <p className="font-bold text-slate-900">{order.order_number}</p>
                                                <p className="text-sm text-slate-500">{new Date(order.created_at).toLocaleDateString()}</p>
                                            </div>
                                            <div className="flex items-center gap-6">
                                                <div className="text-right hidden sm:block">
                                                    <p className="text-sm text-slate-500">Total</p>
                                                    <p className="font-bold text-slate-900">${order.total?.toFixed(2)}</p>
                                                </div>
                                                <div className="text-right">
                                                    <p className="text-sm text-slate-500 mb-1">Status</p>
                                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 capitalize">
                                                        {order.status}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </AccordionTrigger>
                                    <AccordionContent className="px-6 pb-6 pt-2 border-t border-slate-100">
                                        <div className="space-y-4">
                                            {/* Products */}
                                            <div className="space-y-4">
                                                {order.items?.map((item, index) => (
                                                    <div key={index} className="flex gap-4">
                                                        <div className="w-16 h-16 rounded-md bg-slate-50 border border-slate-100 overflow-hidden flex-shrink-0">
                                                            {item.image_url ? (
                                                                <img src={item.image_url} alt={item.name} className="w-full h-full object-cover" />
                                                            ) : (
                                                                <div className="w-full h-full flex items-center justify-center text-slate-300">
                                                                    <ShoppingCart size={20} />
                                                                </div>
                                                            )}
                                                        </div>
                                                        <div className="flex-1 min-w-0 flex flex-col justify-center">
                                                            <p className="text-sm font-medium text-slate-900 line-clamp-2">{item.name}</p>
                                                            <p className="text-sm text-slate-500 mt-1">Qty: {item.quantity}</p>

                                                            {/* Customizations display */}
                                                            {item.customizations && (Array.isArray(item.customizations) ? item.customizations.length > 0 : Object.keys(item.customizations).length > 0) && (
                                                                <div className="mt-2 space-y-1 pl-2 border-l border-slate-100 py-0.5">
                                                                    {Array.isArray(item.customizations) ? (
                                                                        item.customizations.map((cust) => (
                                                                            <div key={cust.id} className="text-[10px]">
                                                                                <div className="flex justify-between font-bold text-slate-600">
                                                                                    <span>{cust.name}</span>
                                                                                    <span className="text-indigo-600 ml-2">+${cust.total.toFixed(2)}</span>
                                                                                </div>
                                                                                {cust.fields.map((f, i) => (
                                                                                    <p key={i} className="text-slate-400 italic leading-tight text-[10px]">
                                                                                        {f.label}: {f.value}
                                                                                    </p>
                                                                                ))}
                                                                            </div>
                                                                        ))
                                                                    ) : (
                                                                        Object.entries(item.customizations).map(([label, val]) => (
                                                                            <p key={label} className="text-[10px] text-slate-400 italic">
                                                                                <span className="font-medium mr-1 text-slate-500">{label}:</span>
                                                                                {val && typeof val === 'string' && val.includes('T') && !isNaN(Date.parse(val))
                                                                                    ? new Date(val).toLocaleDateString()
                                                                                    : val === true ? 'Yes' : val}
                                                                            </p>
                                                                        ))
                                                                    )}
                                                                </div>
                                                            )}
                                                        </div>
                                                        <div className="text-right flex flex-col justify-center flex-shrink-0">
                                                            <span className="font-medium text-slate-900">
                                                                ${((item.display_price || item.base_price || 0) * item.quantity).toFixed(2)}
                                                            </span>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>

                                            {/* Cost Breakdown */}
                                            <div className="border-t border-slate-100 pt-4 space-y-2 text-sm">
                                                <div className="flex justify-between text-slate-600">
                                                    <span>Subtotal</span>
                                                    <span className="font-medium text-slate-900">${(order.subtotal || order.base_total || 0).toFixed(2)}</span>
                                                </div>
                                                <div className="flex justify-between text-slate-600">
                                                    <span className="flex flex-col">
                                                        <span>Shipping</span>
                                                        {order.shipping_method && <span className="text-xs text-slate-400">{order.shipping_method}</span>}
                                                    </span>
                                                    <span className="font-medium text-slate-900">${(order.shipping || order.shipping_cost || 0).toFixed(2)}</span>
                                                </div>
                                                {order.tax > 0 && (
                                                    <div className="flex justify-between text-slate-600">
                                                        <span>Tax</span>
                                                        <span className="font-medium text-slate-900">${order.tax?.toFixed(2)}</span>
                                                    </div>
                                                )}
                                            </div>

                                            <div className="border-t border-slate-100 flex justify-between items-center bg-slate-50 -mx-6 px-6 py-4 mt-4 mb-[-24px]">
                                                <span className="font-bold text-slate-900 text-base">Total</span>
                                                <span className="font-bold text-indigo-600 text-xl">${(order.total_charged || order.total || 0).toFixed(2)}</span>
                                            </div>

                                        </div>
                                    </AccordionContent>
                                </AccordionItem>
                            ))}
                        </Accordion>
                    </div>

                    <div className="text-center mt-8">
                        <Link to={subdomain ? `/store/${subdomain}/shop` : "/products"}>
                            <Button variant="outline" className="rounded-full shadow-sm">Continue Shopping</Button>
                        </Link>
                    </div>
                </div>
            )}
        </div>
    );

    return (
        <div className="animate-fade-in">
            {isMainStore && <Header />}

            <div className={isMainStore ? "mt-20 flex-1 flex" : "flex-1 flex"}>
                {content}
            </div>

            {isMainStore && <Footer />}
        </div>
    );
}
